<template>
  <InertiaHead>
    <title>Register</title>
    <meta
      name="description"
      content="Create your VibeTeams account and build AI-powered teams to automate tasks, collaborate smarter, and accelerate business growth."
    />
    <link rel="canonical" href="https://vibeteams.ai/register" />
  </InertiaHead>

  <AuthBase>
    <div v-if="status" class="mb-4 text-center text-sm font-medium text-green-600">
      {{ status }}
    </div>

    <form
      @submit.prevent="submit"
      class="mx-auto flex w-full max-w-[449px] flex-col gap-4 space-y-5 rounded-2xl border-1 border-eerie-black-400 bg-transparent p-8 sm:gap-5"
    >
      <input type="hidden" name="invitation_token" :value="invitationToken" v-if="invitationToken" />

      <div class="space-y-2 py-2 sm:py-4">
        <h1 class="text-center font-exo-bold text-xl sm:text-2xl">Join the vibe</h1>
        <p class="text-muted-foreground px-2 text-center font-segoe-regular text-xs">Create your account to start collaborating with AI agents</p>
      </div>
      <div class="grid gap-5">
        <div class="grid gap-2">
          <UiLabel for="name">Name</UiLabel>
          <UiInput
            id="name"
            type="text"
            class="h-[42px] w-full rounded-lg border-0 bg-gray-700 font-segoe-regular text-sm"
            required
            autofocus
            :tabindex="1"
            autocomplete="name"
            v-model="form.name"
            placeholder="Full name"
          />
          <InputError :message="form.errors.name" />
        </div>

        <div class="grid gap-2">
          <UiLabel for="email">Email address</UiLabel>
          <UiInput
            id="email"
            type="email"
            class="h-[42px] w-full rounded-lg border-0 bg-gray-700 font-segoe-regular text-sm"
            required
            :tabindex="2"
            autocomplete="email"
            v-model="form.email"
            placeholder="email@example.com"
            :disabled="!!invitedEmail"
          />
          <InputError :message="form.errors.email" />
        </div>

        <div class="grid gap-2">
          <UiLabel for="password">Password</UiLabel>
          <UiInput
            id="password"
            class="h-[42px] w-full rounded-lg border-0 bg-gray-700 font-segoe-regular text-sm"
            type="password"
            required
            :tabindex="3"
            autocomplete="new-password"
            v-model="form.password"
            placeholder="Password"
          />
          <InputError :message="form.errors.password" />
        </div>

        <div class="grid gap-2">
          <UiLabel for="password_confirmation">Confirm password</UiLabel>
          <UiInput
            id="password_confirmation"
            class="h-[42px] w-full rounded-lg border-0 bg-gray-700 font-segoe-regular text-sm"
            type="password"
            required
            :tabindex="4"
            autocomplete="new-password"
            v-model="form.password_confirmation"
            placeholder="Confirm password"
          />
          <InputError :message="form.errors.password_confirmation" />
        </div>

        <div class="space-y-4">
          <!-- Terms Acceptance -->
          <div class="flex items-start gap-3">
            <UiCheckbox id="terms_accepted" v-model="form.terms_accepted" :tabindex="5" class="mt-1" />
            <label for="terms_accepted" class="cursor-pointer text-left font-segoe-regular text-sm text-gray-300">
              I agree to the
              <a href="/terms-of-service" target="_blank" class="text-navy-blue-500 underline hover:text-navy-blue-400">Terms of Service</a>
              and
              <a href="/privacy-policy" target="_blank" class="text-navy-blue-500 underline hover:text-navy-blue-400">Privacy Policy</a>
            </label>
          </div>
          <InputError :message="form.errors.terms_accepted" />

          <InputError :message="form.errors['g-recaptcha-response']" />

          <UiButton variant="gradient" type="submit" class="h-[43px] w-full text-sm" tabindex="6" :disabled="form.processing || isFormIncomplete">
            <LoaderCircle v-if="form.processing" class="h-4 w-4 animate-spin" />
            sign up & vibe
          </UiButton>
          <div class="grid grid-cols-3 items-center justify-center">
            <hr class="text-[#353535]" />
            <div class="flex justify-center">
              <span class="mx-4 text-center font-segoe-regular text-xs text-[#909090]">or sign in with</span>
            </div>
            <hr class="text-[#353535]" />
          </div>
          <a
            :href="route('auth.redirect', 'google')"
            :tabindex="7"
            class="flex w-full cursor-pointer items-center justify-center gap-2 rounded-md border border-[#353535] bg-[#1C1C1C] px-5 py-2"
          >
            <img :src="Google" alt="Google Logo" class="h-6 w-6" />
            <span class="text-center font-segoe-bold text-sm text-white">Google</span>
          </a>

          <div class="text-left text-sm">
            Already vibin'?
            <TextLink :href="route('login')" class="text-navy-blue-500 !no-underline" :tabindex="8">Sign in here</TextLink>
          </div>
        </div>
      </div>

      <UiInput type="hidden" name="g-recaptcha-response" v-model="form['g-recaptcha-response']" />
    </form>
  </AuthBase>
</template>

<script lang="ts">
import Google from '@/../../resources/images/login-register/google.svg';
import InputError from '@/components/common/InputError.vue';
import TextLink from '@/components/common/TextLink.vue';
import { Button as UiButton } from '@/components/ui/button';
import { Checkbox as UiCheckbox } from '@/components/ui/checkbox';
import { Input as UiInput } from '@/components/ui/input';
import { Label as UiLabel } from '@/components/ui/label';
import AuthBase from '@/layouts/AuthLayout.vue';
import { Head as InertiaHead, useForm } from '@inertiajs/vue3';
import { LoaderCircle } from 'lucide-vue-next';

export default {
  components: {
    InputError,
    TextLink,
    UiButton,
    UiCheckbox,
    UiInput,
    UiLabel,
    AuthBase,
    InertiaHead,
    LoaderCircle,
  },
  props: {
    invitationToken: {
      type: String,
      required: false,
    },
    invitedEmail: {
      type: String,
      required: false,
    },
    status: {
      type: String,
      required: false,
    },
    recaptchaSiteKey: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      form: useForm({
        name: '',
        email: this.invitedEmail || '',
        password: '',
        password_confirmation: '',
        terms_accepted: false,
        'g-recaptcha-response': '',
        invitation_token: this.invitationToken || '',
      }),
      Google,
    };
  },
  computed: {
    isFormIncomplete() {
      return (
        this.form?.processing ||
        !this.form?.name ||
        !this.form?.email ||
        !this.form?.password ||
        !this.form?.password_confirmation ||
        !this.form?.terms_accepted
      );
    },
  },
  methods: {
    async submit() {
      try {
        const recaptchaToken = await new Promise<string>((resolve, reject) => {
          grecaptcha.ready(() => {
            grecaptcha.execute(this.recaptchaSiteKey, { action: 'register' }).then(resolve).catch(reject);
          });
        });

        this.form['g-recaptcha-response'] = recaptchaToken;

        if (this.invitationToken) {
          this.form.invitation_token = this.invitationToken;
        }

        this.form.post(route('register'), {
          onFinish: () => this.form.reset('password', 'password_confirmation', 'g-recaptcha-response'),
        });
      } catch (error) {
        console.error('reCAPTCHA execution failed:', error);
      }
    },
  },
};
</script>
